import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { Eye, EyeOff, KeyRound, Save, UserCircle, Check, X } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20";
const REGLAS = [
  ["8 caracteres o más", (p) => p.length >= 8],
  ["Una mayúscula", (p) => /[A-Z]/.test(p)],
  ["Una minúscula", (p) => /[a-z]/.test(p)],
  ["Un número", (p) => /\d/.test(p)],
];

function Campo({ label, children }) {
  return <div><label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>{children}</div>;
}

function DatosPerfil({ perfil, onSaved }) {
  const [f, setF] = useState({ name: perfil.name || "", cargo: perfil.cargo || "", email: perfil.email || "", telefono: perfil.telefono || "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const guardar = async (e) => {
    e.preventDefault();
    if (f.email.trim().toLowerCase() !== perfil.email && !window.confirm(`Tu usuario para ingresar a Roge cambiará a ${f.email.trim().toLowerCase()}. ¿Confirmas la actualización?`)) return;
    setSaving(true);
    try { const r = await api.put("/perfil", f); onSaved(r.data); toast.success("Tus datos se actualizaron correctamente"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={guardar} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="perfil-datos">
      <div className="flex items-center gap-2"><UserCircle className="h-5 w-5 text-vino-700" /><h2 className="text-base font-semibold text-slate-800 md:text-lg">Mis datos</h2></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo label="Nombre completo *"><input required value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="perfil-nombre" /></Campo>
        <Campo label="Cargo"><input value={f.cargo} onChange={(e) => set("cargo", e.target.value)} className={inputCls} data-testid="perfil-cargo" /></Campo>
        <Campo label="Correo electrónico (usuario de acceso) *"><input required type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="perfil-email" /></Campo>
        <Campo label="Número de teléfono"><input value={f.telefono} onChange={(e) => set("telefono", e.target.value)} className={inputCls} data-testid="perfil-telefono" /></Campo>
        <Campo label="Empresa"><p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="perfil-empresa">{perfil.empresa || "—"}{perfil.empresa_usuaria ? ` · Empresa usuaria: ${perfil.empresa_usuaria}` : ""}</p></Campo>
        <Campo label="Rol en Roge"><p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700" data-testid="perfil-rol">{perfil.role_label}</p></Campo>
      </div>
      {perfil.role === "empleado" && <p className="text-xs text-slate-500">Estos datos son los de tu usuario de acceso. Tu hoja de vida y tu información laboral no cambian desde aquí.</p>}
      <div className="flex justify-end"><button type="submit" disabled={saving} data-testid="perfil-guardar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">{saving ? <Spinner className="h-4 w-4" /> : <Save className="h-4 w-4" />} Guardar cambios</button></div>
    </form>
  );
}

function PassInput({ value, onChange, testid }) {
  const [ver, setVer] = useState(false);
  return (
    <div className="relative">
      <input type={ver ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} pr-10`} data-testid={testid} autoComplete="new-password" />
      <button type="button" onClick={() => setVer((v) => !v)} data-testid={`${testid}-ver`} aria-label={ver ? "Ocultar" : "Mostrar"} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-600">{ver ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
    </div>
  );
}

function CambiarPassword({ refEl, onChanged }) {
  const [f, setF] = useState({ actual: "", nueva: "", confirmar: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const ok = REGLAS.every(([, t]) => t(f.nueva)) && f.nueva === f.confirmar && f.actual;
  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { const r = await api.post("/perfil/password", f); if (r.data.token) localStorage.setItem("roge_token", r.data.token); setF({ actual: "", nueva: "", confirmar: "" }); onChanged(); toast.success("Tu contraseña se cambió correctamente. Cerramos tus otras sesiones."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <form ref={refEl} onSubmit={guardar} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="perfil-password">
      <div className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-vino-700" /><h2 className="text-base font-semibold text-slate-800 md:text-lg">Cambiar contraseña</h2></div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Campo label="Contraseña actual"><PassInput value={f.actual} onChange={set("actual")} testid="password-actual" /></Campo>
        <Campo label="Nueva contraseña"><PassInput value={f.nueva} onChange={set("nueva")} testid="password-nueva" /></Campo>
        <Campo label="Confirmar nueva contraseña"><PassInput value={f.confirmar} onChange={set("confirmar")} testid="password-confirmar" /></Campo>
      </div>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="password-reglas">
        {[...REGLAS, ["Las contraseñas coinciden", () => f.nueva && f.nueva === f.confirmar]].map(([l, t]) => {
          const cumple = t(f.nueva);
          return <li key={l} className={`flex items-center gap-1 ${cumple ? "text-emerald-700" : "text-slate-400"}`}>{cumple ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}{l}</li>;
        })}
      </ul>
      <p className="text-xs text-slate-500">Si olvidas tu contraseña, solicita el restablecimiento a HUMAN RESOURCES MANAGMENT SA</p>
      <div className="flex justify-end"><button type="submit" disabled={!ok || saving} data-testid="password-guardar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50">{saving ? <Spinner className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />} Guardar nueva contraseña</button></div>
    </form>
  );
}

export default function Perfil() {
  const { setUser } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [params] = useSearchParams();
  const passRef = useRef(null);
  useEffect(() => { api.get("/perfil").then((r) => setPerfil(r.data)).catch(() => toast.error("No se pudo cargar tu perfil")); }, []);
  useEffect(() => {
    if (perfil && params.get("seccion") === "password") passRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [perfil, params]);
  if (!perfil) return <div className="flex h-48 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;
  const actualizar = (p) => { setPerfil(p); setUser((u) => ({ ...u, ...p })); };
  return (
    <div className="space-y-5" data-testid="perfil-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Mi perfil</h1>
        <p className="mt-1 text-sm text-slate-500">Consulta y actualiza tus datos de acceso a Roge.</p>
      </div>
      <DatosPerfil perfil={perfil} onSaved={actualizar} />
      <CambiarPassword refEl={passRef} onChanged={() => actualizar({ ...perfil, password_predeterminada: false })} />
    </div>
  );
}
